import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, isApiError } from "./http";
import { queryKeys } from "./query";
import type { Membership, Persona, Session, SessionTenant, SignInGate, SsoOptions, StepUpChallenge } from "./types";

const EMPTY_SESSION: Session = { authenticated: false, memberships: [], roles: [], permissions: [], auth: { devLogin: false, oidc: false } };

export interface SessionApi {
  /** `undefined` while the first request is in flight. */
  session: Session | undefined;
  isLoading: boolean;
  /** The session endpoint failed (service unavailable, or an unexpected error). */
  error: unknown;
  isAuthenticated: boolean;
  user: Session["user"];
  tenant: SessionTenant | undefined;
  memberships: Membership[];
  permissions: ReadonlySet<string>;
  /** Optional product modules switched on for the workspace in the platform console. */
  modules: ReadonlySet<string>;
  hasModule: (module: Module) => boolean;
  refetch: () => Promise<unknown>;
  devLogin: (username: string) => Promise<Session>;
  /** Ends the session. Resolves `true` when the browser is leaving for the identity provider's sign-out page. */
  logout: () => Promise<boolean>;
  switchTenant: (tenantId: string) => Promise<Session>;
  startStepUp: () => Promise<StepUpChallenge>;
  completeStepUp: (challengeId: string, code: string) => Promise<Session>;
  /** URL of the OIDC sign-in (UAE Government SSO) that returns to `returnUrl`; the identity provider's pages follow `locale`. */
  ssoUrl: (returnUrl: string, locale?: string, options?: SsoOptions) => string;
}

const SessionContext = createContext<SessionApi | null>(null);

/**
 * `GET /api/auth/session` answers `{ authenticated: false, auth }` for anonymous callers. A bare 401 is read the same way; its sign-in
 * methods are unknown, so both are offered and the server decides.
 */
export async function fetchSession(): Promise<Session> {
  try {
    return await api.get<Session>("/api/auth/session");
  } catch (e) {
    if (isApiError(e) && e.status === 401) return { ...EMPTY_SESSION, auth: { devLogin: true, oidc: true } };
    throw e;
  }
}

/**
 * Loads `GET /api/auth/session` once and exposes it with the sign-in, sign-out, tenant switch and step-up actions. Every action that
 * changes the principal writes the returned session into the cache and invalidates all other queries, so no data from the previous
 * tenant survives a switch. The tenant accent is applied to `<html>` as `--tenant-accent`.
 */
export function SessionProvider({ children, applyAccent = true }: { children: ReactNode; applyAccent?: boolean }) {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: queryKeys.session, queryFn: fetchSession, staleTime: 60_000, retry: 1 });

  const adopt = useCallback(
    async (session: Session) => {
      qc.setQueryData(queryKeys.session, session);
      await qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== queryKeys.session[0] });
      return session;
    },
    [qc],
  );

  const devLoginM = useMutation({ mutationFn: (username: string) => api.post<Session>("/api/auth/dev-login", { username }), meta: { errorToast: false } });
  const switchM = useMutation({ mutationFn: (tenantId: string) => api.post<Session>("/api/auth/switch-tenant", { tenantId }) });
  const stepUpM = useMutation({ mutationFn: (v: { challengeId: string; code: string }) => api.post<Session>("/api/auth/step-up", v), meta: { errorToast: false } });

  const session = query.data;
  const tenant = session?.authenticated ? session.tenant : undefined;

  useEffect(() => {
    if (!applyAccent || typeof document === "undefined") return;
    const root = document.documentElement;
    if (tenant?.accent) root.style.setProperty("--tenant-accent", tenant.accent);
    else root.style.removeProperty("--tenant-accent");
    if (tenant?.kind) root.dataset.tenantKind = tenant.kind;
    else delete root.dataset.tenantKind;
  }, [applyAccent, tenant?.accent, tenant?.kind]);

  const value = useMemo<SessionApi>(() => {
    const modules = new Set<string>(session?.modules ?? []);
    return {
      session,
      isLoading: query.isLoading,
      error: query.error,
      isAuthenticated: !!session?.authenticated,
      user: session?.authenticated ? session.user : undefined,
      tenant,
      memberships: session?.memberships ?? [],
      permissions: new Set(session?.permissions ?? []),
      modules,
      hasModule: (module) => modules.has(module),
      refetch: () => query.refetch(),
      devLogin: async (username) => adopt(await devLoginM.mutateAsync(username)),
      logout: async () => {
        let redirect: string | null | undefined;
        try {
          ({ redirect } = await api.post<{ redirect?: string | null }>("/api/auth/logout"));
        } finally {
          // An SSO session is ended at the identity provider too, which returns to /login; the page unloads on the way.
          if (!redirect) {
            qc.clear();
            qc.setQueryData(queryKeys.session, { ...EMPTY_SESSION, auth: session?.auth ?? EMPTY_SESSION.auth });
          }
        }
        if (!redirect) return false;
        window.location.assign(redirect);
        return true;
      },
      switchTenant: async (tenantId) => adopt(await switchM.mutateAsync(tenantId)),
      startStepUp: () => api.post<StepUpChallenge>("/api/auth/step-up/challenge"),
      completeStepUp: async (challengeId, code) => {
        const next = await stepUpM.mutateAsync({ challengeId, code });
        qc.setQueryData(queryKeys.session, next);
        return next;
      },
      ssoUrl: (returnUrl, locale, options) => {
        const query = new URLSearchParams({ returnUrl });
        if (locale) query.set("locale", locale);
        if (options?.idp) query.set("idp", options.idp);
        if (options?.ws) query.set("ws", options.ws);
        return `/api/auth/login?${query.toString()}`;
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, tenant, query.isLoading, query.error, adopt]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionApi {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside <SessionProvider>");
  return ctx;
}

/** Whether the signed-in user holds a permission in the current tenant (`"*"` grants everything). */
export function usePermission(permission: string | readonly string[] | undefined, mode: "all" | "any" = "all"): boolean {
  const { permissions } = useSession();
  if (!permission) return true;
  const list = typeof permission === "string" ? [permission] : permission;
  if (permissions.has("*")) return true;
  return mode === "all" ? list.every((p) => permissions.has(p)) : list.some((p) => permissions.has(p));
}

/** Non-hook check for building menus from a permission set. */
export function hasPermission(permissions: ReadonlySet<string>, permission: string | readonly string[] | undefined, mode: "all" | "any" = "all"): boolean {
  if (!permission) return true;
  if (permissions.has("*")) return true;
  const list = typeof permission === "string" ? [permission] : permission;
  return mode === "all" ? list.every((p) => permissions.has(p)) : list.some((p) => permissions.has(p));
}

/** Optional product modules (services/src/Policy.Application/Tenancy/TenantModules.cs), switched on per workspace in the console. */
export const Modules = {
  RegLab: "reglab",
} as const;
export type Module = (typeof Modules)[keyof typeof Modules];

/** Whether an optional module is switched on for the current workspace. */
export function useModule(module: Module): boolean {
  return useSession().hasModule(module);
}

/** A ministry's sign-in gate (`/login?ws=CODE`); `null` when the code is not a gate. */
export function useSignInGate(code: string | null | undefined) {
  return useQuery({
    queryKey: ["auth", "gate", code ?? ""],
    queryFn: async () => {
      try {
        return await api.get<SignInGate>(`/api/auth/gates/${encodeURIComponent(code ?? "")}`);
      } catch (e) {
        if (isApiError(e) && e.status === 404) return null;
        throw e;
      }
    },
    enabled: !!code,
    staleTime: 5 * 60_000,
    retry: 0,
  });
}

/** Development personas (`GET /api/auth/personas`); only requested when the session reports `auth.devLogin`. */
export function usePersonas(enabled: boolean) {
  return useQuery({ queryKey: queryKeys.personas, queryFn: () => api.get<Persona[]>("/api/auth/personas"), enabled, staleTime: 5 * 60_000, retry: 0 });
}

/** Permission keys (services/src/Policy.Application/Security/Permissions.cs). */
export const Permissions = {
  PoliciesRead: "policies.read",
  PoliciesCreate: "policies.create",
  PoliciesEdit: "policies.edit",
  PoliciesDelete: "policies.delete",
  PoliciesShare: "policies.share",
  PoliciesDisclose: "policies.disclose",
  RecordsEdit: "records.edit",
  ProposalsReview: "proposals.review",
  VersionsEdit: "versions.edit",
  CommentsWrite: "comments.write",
  GatesDecide: "gates.decide",
  GatesSubmit: "gates.submit",
  ConformanceAttest: "conformance.attest",
  ConformanceWaive: "conformance.waive",
  AgentsRun: "agents.run",
  ConsultationManage: "consultation.manage",
  ConsultationRespond: "consultation.respond",
  MonitoringEdit: "monitoring.edit",
  RegLabManage: "reglab.manage",
  MembersManage: "members.manage",
  WorkspaceSettings: "workspace.settings",
  TemplatesManage: "templates.manage",
  OversightPipeline: "oversight.pipeline",
  OversightReview: "oversight.review",
  OversightDecide: "oversight.decide",
  OversightCabinet: "oversight.cabinet",
  OversightRegistry: "oversight.registry",
  OversightOverlaps: "oversight.overlaps",
  OversightRegLab: "oversight.reglab",
  OversightAnalytics: "oversight.analytics",
  OversightGovernance: "oversight.governance",
  OversightAnalysis: "oversight.analysis",
  SignalsRead: "signals.read",
  SignalsManage: "signals.manage",
  KnowledgeRead: "knowledge.read",
  KnowledgeManage: "knowledge.manage",
  AcademyUse: "academy.use",
  AcademyManage: "academy.manage",
  AuditRead: "audit.read",
  ConsoleTenants: "console.tenants",
  ConsoleIdentity: "console.identity",
  ConsoleRoles: "console.roles",
  ConsoleWorkflows: "console.workflows",
  ConsoleAgents: "console.agents",
  ConsoleRegistry: "console.registry",
  ConsoleAiGovernance: "console.aigov",
  ConsoleAudit: "console.audit",
  ConsoleKnowledge: "console.knowledge",
  ConsoleReleases: "console.releases",
} as const;
export type Permission = (typeof Permissions)[keyof typeof Permissions];
